<script setup>
import { computed, ref } from 'vue'
import moment from 'moment'
import { DRAFT_LABEL } from '@/utils/requestApproval'

const props = defineProps({
  restaurant: { type: Object, default: null },
  bookingMember: { type: Object, default: null },
  summary: { type: Object, default: null },
  // RM-253 — set once the draft is saved (as soon as a restaurant is chosen).
  reservationDraftId: { type: String, default: null },
})

const expanded = ref(false)

const hasContent = computed(() => !!(
  props.restaurant
  || props.bookingMember
  || props.reservationDraftId
  || (props.summary && Object.keys(props.summary).some(k => k !== 'updatedAt'))
))

const isSaved = computed(() => !!props.reservationDraftId)

function formatDate(iso) {
  if (!iso) return ''
  const m = moment(iso, 'YYYY-MM-DD', true)
  if (!m.isValid()) return iso

  const today = moment().startOf('day')
  const target = m.clone().startOf('day')
  const diff = target.diff(today, 'days')
  if (diff === 0) return `Today, ${m.format('ddd, MMM D')}`
  if (diff === 1) return `Tomorrow, ${m.format('ddd, MMM D')}`

  const sameYear = m.year() === today.year()

  return m.format(sameYear ? 'ddd, MMM D' : 'ddd, MMM D, YYYY')
}

function shortDate(iso) {
  if (!iso) return ''
  const m = moment(iso, 'YYYY-MM-DD', true)
  if (!m.isValid()) return iso

  return m.format('ddd M/D')
}

function formatTime(t) {
  if (!t) return ''

  return String(t).replace(/(am|pm)$/i, m => m[0].toLowerCase())
}

function formatMoney(n) {
  if (n == null) return ''

  return `$${Number(n).toLocaleString('en-US')}`
}

const timeDisplay = computed(() => {
  const s = props.summary || {}
  const preferred = formatTime(s.preferredTime)
  const earliest = formatTime(s.earliestTime)
  const latest = formatTime(s.latestTime)
  if (preferred && earliest && latest && (earliest !== preferred || latest !== preferred)) {
    return { primary: preferred, range: `${earliest}–${latest}` }
  }
  if (preferred) return { primary: preferred, range: '' }
  if (earliest || latest) return { primary: [earliest, latest].filter(Boolean).join('–'), range: '' }

  return { primary: '', range: '' }
})

const collapsedChips = computed(() => {
  const r = props.restaurant
  const s = props.summary || {}
  const chips = []
  if (r?.restaurantName) chips.push(r.restaurantName)
  if (s.date) chips.push(shortDate(s.date))
  if (s.preferredTime) chips.push(formatTime(s.preferredTime))
  if (s.partySize) chips.push(s.partySize === 1 ? '1 guest' : `${s.partySize}`)

  return chips
})

const restaurantLocation = computed(() => {
  const r = props.restaurant
  if (!r) return ''

  return r.market || ''
})

function toggle() {
  expanded.value = !expanded.value
}
</script>

<template>
  <div
    v-if="hasContent"
    class="agent-summary"
    :class="{ 'agent-summary--expanded': expanded }"
  >
    <button
      type="button"
      class="agent-summary__row agent-summary__row--collapsed"
      :aria-expanded="expanded"
      aria-controls="agent-summary-details"
      @click="toggle"
    >
      <VIcon
        icon="mdi-clipboard-text-outline"
        size="16"
        class="agent-summary__icon"
      />
      <span class="agent-summary__chips">
        <template
          v-for="(chip, i) in collapsedChips"
          :key="i"
        >
          <span class="agent-summary__chip">{{ chip }}</span>
          <span
            v-if="i < collapsedChips.length - 1"
            class="agent-summary__sep"
            aria-hidden="true"
          >·</span>
        </template>
        <span
          v-if="collapsedChips.length === 0"
          class="agent-summary__chip agent-summary__chip--muted"
        >
          Reservation request in progress
        </span>
      </span>
      <!--
        Persistence marker, not an action: metadata weight (12px, tertiary) so
        it never competes with the request chips, and plain text because this
        row is itself the expand button — a link nested in a button would be
        invalid markup and a broken tap target. The actionable "View" lives in
        the expanded panel below.
      -->
      <span
        v-if="isSaved"
        class="agent-summary__saved"
      >In Bookings</span>
      <VIcon
        :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
        size="20"
        class="agent-summary__chev"
      />
    </button>

    <div
      v-show="expanded"
      id="agent-summary-details"
      class="agent-summary__details"
    >
      <dl class="agent-summary__list">
        <template v-if="restaurant?.restaurantName">
          <dt>Restaurant</dt>
          <dd>
            <span class="agent-summary__value">{{ restaurant.restaurantName }}</span>
            <span
              v-if="restaurantLocation"
              class="agent-summary__meta"
            >{{ restaurantLocation }}</span>
          </dd>
        </template>

        <template v-if="bookingMember?.firstName">
          <dt>For</dt>
          <dd>
            <span class="agent-summary__value">{{ bookingMember.firstName }}<template v-if="bookingMember.lastName"> {{ bookingMember.lastName }}</template></span>
          </dd>
        </template>

        <template v-if="summary?.date">
          <dt>Date</dt>
          <dd>
            <span class="agent-summary__value">{{ formatDate(summary.date) }}</span>
          </dd>
        </template>

        <template v-if="timeDisplay.primary">
          <dt>Time</dt>
          <dd>
            <span class="agent-summary__value">{{ timeDisplay.primary }}</span>
            <span
              v-if="timeDisplay.range"
              class="agent-summary__meta"
            >({{ timeDisplay.range }})</span>
          </dd>
        </template>

        <template v-if="summary?.partySize">
          <dt>Party</dt>
          <dd>
            <span class="agent-summary__value">{{ summary.partySize === 1 ? '1 guest' : `${summary.partySize} guests` }}</span>
          </dd>
        </template>

        <template v-if="summary?.occasion">
          <dt>Occasion</dt>
          <dd>
            <span class="agent-summary__value">{{ summary.occasion }}</span>
          </dd>
        </template>

        <template v-if="summary?.perPersonSpend">
          <dt>Spend</dt>
          <dd>
            <span class="agent-summary__value">{{ formatMoney(summary.perPersonSpend) }}<span class="agent-summary__unit">pp</span></span>
            <span
              v-if="summary.totalSpend"
              class="agent-summary__meta"
            >{{ formatMoney(summary.totalSpend) }} total</span>
          </dd>
        </template>

        <template v-if="summary?.notes">
          <dt>Notes</dt>
          <dd>
            <span class="agent-summary__value agent-summary__value--multiline">{{ summary.notes }}</span>
          </dd>
        </template>

        <template v-if="isSaved">
          <dt>Saved</dt>
          <dd>
            <span class="agent-summary__value">{{ DRAFT_LABEL }}</span>
            <RouterLink
              to="/bookings"
              class="agent-summary__link"
            >
              View
            </RouterLink>
            <span class="agent-summary__meta">Nothing is sent until you submit</span>
          </dd>
        </template>
      </dl>
    </div>
  </div>
</template>

<style scoped>
.agent-summary {
  flex-shrink: 0;
  background: var(--ds-surface-card, #fff);
  border-bottom: 1px solid var(--ds-border-light, rgba(0, 0, 0, 0.08));
}

.agent-summary__row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  /* Share the chat column's container so the restaurant name lines up with the
     messages and the chevron sits on the column's right edge, not the window's.
     The row's white background and bottom border stay full-bleed — this
     constrains the CONTENT only. Mobile is unaffected: the viewport is
     narrower than the cap, so the row is already full width.

     752, not 720: the messages sit in a 720px column, and this row adds its own
     16px of padding inside the cap. 720 + (2 x 16) puts the text's left edge at
     (W - 720) / 2 — exactly the message column's edge — and the chevron on its
     right edge. */
  max-width: 752px;
  margin-inline: auto;
  min-height: 44px;
  padding: 8px 16px;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: var(--ds-text-primary, #111);
  -webkit-tap-highlight-color: transparent;
}

.agent-summary__row:hover {
  background: var(--ds-surface-inset, rgba(0, 0, 0, 0.03));
}

.agent-summary__row:focus-visible {
  outline: 2px solid var(--ds-brand-primary-dark, #0FA091);
  outline-offset: -2px;
}

.agent-summary__icon {
  flex-shrink: 0;
  color: var(--ds-brand-primary-darker, #0FA091);
}

.agent-summary__chips {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 6px;
  row-gap: 2px;
  font-size: 13px;
  line-height: 1.3;
}

.agent-summary__chip {
  font-weight: 600;
  color: var(--ds-text-primary, #111);
  white-space: nowrap;
}

.agent-summary__chip--muted {
  font-weight: 500;
  color: var(--ds-text-secondary, rgba(0, 0, 0, 0.6));
}

.agent-summary__sep {
  color: var(--ds-text-tertiary, rgba(0, 0, 0, 0.38));
  user-select: none;
}

/* Metadata weight: smaller and lighter than the 13px/600 request chips, so the
   scan order stays details-first. Never wraps below its own line. */
.agent-summary__saved {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
  color: var(--ds-text-tertiary, rgba(0, 0, 0, 0.5));
}

.agent-summary__chev {
  flex-shrink: 0;
  color: var(--ds-text-secondary, rgba(0, 0, 0, 0.55));
  transition: transform 0.2s ease;
}

.agent-summary--expanded .agent-summary__row {
  border-bottom: 1px solid var(--ds-border-light, rgba(0, 0, 0, 0.06));
}

.agent-summary__details {
  /* Same column as the collapsed row above it, so expanding does not shift the
     content sideways. The subtle background still spans the full width.
     Same 720 + 2 x 16 arithmetic as the row above. */
  max-width: 752px;
  margin-inline: auto;
  padding: 12px 16px 16px;
  background: var(--ds-surface-subtle, #fafafa);
}

.agent-summary__list {
  display: grid;
  grid-template-columns: 92px 1fr;
  column-gap: 12px;
  row-gap: 8px;
  margin: 0;
}

.agent-summary__list dt {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  color: var(--ds-text-tertiary, rgba(0, 0, 0, 0.5));
  align-self: start;
  padding-top: 1px;
}

.agent-summary__list dd {
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 8px;
  row-gap: 2px;
  min-width: 0;
}

.agent-summary__value {
  font-size: 14px;
  font-weight: 600;
  color: var(--ds-text-primary, #111);
  line-height: 1.35;
  word-break: break-word;
}

.agent-summary__value--multiline {
  white-space: pre-wrap;
  font-weight: 500;
}

.agent-summary__unit {
  font-weight: 500;
  font-size: 12px;
  color: var(--ds-text-secondary, rgba(0, 0, 0, 0.55));
  margin-left: 1px;
}

.agent-summary__meta {
  font-size: 12px;
  font-weight: 500;
  color: var(--ds-text-secondary, rgba(0, 0, 0, 0.55));
}

.agent-summary__link {
  font-size: 12px;
  font-weight: 600;
  color: var(--ds-brand-primary-dark, #0FA091);
  text-decoration: none;
  /* 44px tap target without disturbing the baseline-aligned row */
  padding: 12px 4px;
  margin: -12px -4px;
}

.agent-summary__link:hover {
  text-decoration: underline;
}

.agent-summary__link:focus-visible {
  outline: 2px solid var(--ds-brand-primary-dark, #0FA091);
  outline-offset: 2px;
  border-radius: 4px;
}

/* Narrow phones: drop dt to its own line for breathing room */
@media (max-width: 360px) {
  .agent-summary__list {
    grid-template-columns: 1fr;
    row-gap: 10px;
  }

  .agent-summary__list dt {
    margin-bottom: -4px;
  }
}
</style>
